import React from 'react';
import { Helmet } from 'react-helmet-async';

interface ArticleSchemaProps {
  title: string;
  description: string;
  author: string;
  publishedDate: string;
  modifiedDate?: string;
  imageUrl?: string;
  url: string;
  category: string;
  keywords?: string;
  wordCount?: number;
  content?: string;
}

const ArticleSchema: React.FC<ArticleSchemaProps> = ({
  title,
  description,
  author,
  publishedDate,
  modifiedDate,
  imageUrl,
  url,
  category,
  keywords,
  wordCount,
  content
}) => {
  // Calculate word count if not provided and content is available
  const calculatedWordCount = wordCount || (content ? content.split(/\s+/).length : undefined);

  // Ensure image URL is absolute
  const absoluteImageUrl = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `https://festutrustning.se${imageUrl}`)
    : 'https://festutrustning.se/og-image.jpg';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    description: description,
    image: {
      '@type': 'ImageObject',
      url: absoluteImageUrl,
      width: 1200,
      height: 630
    },
    datePublished: publishedDate,
    dateModified: modifiedDate || publishedDate,
    author: {
      '@type': 'Person',
      name: author,
      url: 'https://festutrustning.se/om-oss',
      jobTitle: 'Content Manager',
      worksFor: {
        '@type': 'Organization',
        name: 'festutrustning.se'
      }
    },
    publisher: {
      '@type': 'Organization',
      name: 'festutrustning.se',
      url: 'https://festutrustning.se',
      description: 'Professionell festutrustning för uthyrning i Skåne',
      logo: {
        '@type': 'ImageObject',
        url: 'https://festutrustning.se/logo.png',
        width: 250,
        height: 60
      },
      sameAs: [
        'https://www.facebook.com/festutrustning',
        'https://www.instagram.com/festutrustning'
      ]
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    },
    articleSection: category,
    inLanguage: 'sv-SE',
    ...(keywords && { keywords: keywords }),
    ...(calculatedWordCount && { wordCount: calculatedWordCount }),
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.blog-content']
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Hem',
        item: 'https://festutrustning.se'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Nyheter',
        item: 'https://festutrustning.se/nyheter'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: url
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default ArticleSchema;
